/**
 * De-paras — onde a regra de negócio deixa de ser código.
 *
 * No `Central.R` os mapeamentos posto→distribuidora estavam hardcoded em quatro
 * `case_when` diferentes, com conflitos reais entre si. Esta tela é o motivo
 * pelo qual isso não volta a acontecer: a área de negócio mantém o mapeamento
 * sozinha, e mudar um de-para não exige deploy.
 *
 * Dois cuidados herdados aparecem aqui:
 *
 * - **Chave normalizada.** O backend faz `strip()` + caixa alta + colapso de
 *   espaços na escrita. Digitar `"Diesel S10 "` cria um cadastro que casa, e
 *   não um natimorto. O campo avisa disso antes de salvar.
 * - **Conflito marcado.** A carga inicial marcou os mapeamentos em que o R
 *   divergia de si mesmo (`CBA 1` → ITAÚ num bloco, → SHELL noutro). Eles
 *   aparecem sinalizados até alguém do comercial decidir.
 */

import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { Botao } from '../../components/Botao'
import { Cartao } from '../../components/Cartao'
import { Entrada, Selecao } from '../../components/Campo'
import { Aviso, Carregando, Erro } from '../../components/Estado'
import { Pagina, Pilha } from '../../components/Pagina'
import { Tabela, type Coluna } from '../../components/Tabela'
import { useBases, useDistribuidoras, usePracas, useProdutos } from '../../lib/catalogo'
import { contagem, dataHora } from '../../lib/formato'
import { useDeParas, useRemoverDePara, useSalvarDePara, type TipoDePara } from './hooks'
import estilos from './Ingestao.module.css'

type Registro = {
  id: number
  origem_texto: string
  observacao: string
  atualizado_em: string
  conflito?: boolean
} & Record<string, unknown>

const ABAS: { tipo: TipoDePara; rotulo: string; campoAlvo: string; rotuloAlvo: string }[] = [
  { tipo: 'produto', rotulo: 'Produto', campoAlvo: 'produto', rotuloAlvo: 'Produto canônico' },
  {
    tipo: 'distribuidora',
    rotulo: 'Distribuidora',
    campoAlvo: 'distribuidora',
    rotuloAlvo: 'Distribuidora',
  },
  { tipo: 'praca', rotulo: 'Praça', campoAlvo: 'praca', rotuloAlvo: 'Praça canônica' },
  // Entrou na Fase 5. Até então `base` era o único resolver sem de-para, na
  // aposta de que os nomes viriam só da ABICOM e seriam estáveis — o custo de
  // refinaria do Terra Brasil, digitado à mão, escreve `Paulinia`.
  { tipo: 'base', rotulo: 'Base', campoAlvo: 'base', rotuloAlvo: 'Base de suprimento' },
]

export function DeParasPage() {
  const [parametros, setParametros] = useSearchParams()
  const tipo = (parametros.get('tipo') ?? 'produto') as TipoDePara
  const aba = ABAS.find((a) => a.tipo === tipo) ?? ABAS[0]

  const [busca, setBusca] = useState('')
  const [pagina, setPagina] = useState(1)
  const [emEdicao, setEmEdicao] = useState<Registro | null>(null)

  const consulta = useDeParas<Registro>(aba.tipo, busca, pagina)
  const remover = useRemoverDePara(aba.tipo)

  const produtos = useProdutos()
  const distribuidoras = useDistribuidoras()
  const pracas = usePracas()
  const bases = useBases()

  // Um mapa e não uma escada de ternários: com quatro de-paras a escada passa a
  // esconder qual ramo é qual, e o próximo de-para a entrar iria para o fim
  // dela por inércia.
  const alvos = {
    produto: (produtos.data ?? []).map((p) => ({ valor: String(p.id), rotulo: p.nome })),
    distribuidora: (distribuidoras.data ?? []).map((d) => ({
      valor: String(d.id),
      rotulo: d.nome,
    })),
    praca: (pracas.data ?? []).map((p) => ({ valor: String(p.id), rotulo: `${p.nome}/${p.uf}` })),
    base: (bases.data ?? []).map((b) => ({ valor: String(b.id), rotulo: `${b.nome}/${b.uf}` })),
  }[aba.tipo]

  function trocarAba(proximo: TipoDePara) {
    setParametros({ tipo: proximo }, { replace: true })
    setBusca('')
    setPagina(1)
    setEmEdicao(null)
  }

  const nomeAlvo = `${aba.tipo}Nome` // produtoNome, distribuidoraNome, …

  const colunas: Coluna<Registro>[] = [
    {
      chave: 'origem',
      cabecalho: 'Texto que vem na planilha',
      celula: (linha) => <code className={estilos.valorBruto}>{linha.origem_texto}</code>,
    },
    {
      chave: 'alvo',
      cabecalho: aba.rotuloAlvo,
      celula: (linha) => (
        <span className={estilos.alvo}>
          {String(linha[nomeAlvo] ?? '—')}
          {linha.conflito && (
            <span className={estilos.conflito} title="Mapeamento divergente herdado do R">
              conflito
            </span>
          )}
        </span>
      ),
    },
    {
      chave: 'observacao',
      cabecalho: 'Observação',
      celula: (linha) => <span className={estilos.detalhe}>{linha.observacao || '—'}</span>,
    },
    {
      chave: 'atualizado',
      cabecalho: 'Atualizado',
      largura: '140px',
      celula: (linha) => (
        <span className={estilos.arquivoMeta}>{dataHora(linha.atualizado_em)}</span>
      ),
    },
    {
      chave: 'acoes',
      cabecalho: '',
      largura: '140px',
      celula: (linha) => (
        <div className={estilos.acoesLinha}>
          <Botao tamanho="pequeno" variante="sutil" onClick={() => setEmEdicao(linha)}>
            Editar
          </Botao>
          <Botao
            tamanho="pequeno"
            variante="perigo"
            onClick={() => {
              if (
                window.confirm(
                  `Apagar o de-para de "${linha.origem_texto}"? As linhas que dependiam dele voltam a ser pendência no próximo reprocessamento.`,
                )
              ) {
                remover.mutate(linha.id)
              }
            }}
          >
            Apagar
          </Botao>
        </div>
      ),
    },
  ]

  const total = consulta.data?.count ?? 0
  const paginas = Math.max(1, Math.ceil(total / 50))

  return (
    <Pagina
      titulo="De-paras"
      descricao="A tradução entre a grafia da planilha e o cadastro canônico. É dado, não código: uma grafia nova é uma linha nova, não um deploy."
    >
      <nav className={estilos.abas} aria-label="Tipo de de-para">
        {ABAS.map((item) => (
          <button
            key={item.tipo}
            type="button"
            className={`${estilos.aba} ${item.tipo === aba.tipo ? estilos.abaAtiva : ''}`}
            onClick={() => trocarAba(item.tipo)}
            aria-current={item.tipo === aba.tipo ? 'page' : undefined}
          >
            {item.rotulo}
          </button>
        ))}
      </nav>

      <Pilha>
        <Formulario
          key={`${aba.tipo}-${emEdicao?.id ?? 'novo'}`}
          tipo={aba.tipo}
          campoAlvo={aba.campoAlvo}
          rotuloAlvo={aba.rotuloAlvo}
          alvos={alvos}
          registro={emEdicao}
          aoFechar={() => setEmEdicao(null)}
        />

        {remover.isError && <Erro erro={remover.error} />}

        <Cartao
          titulo={`${contagem(total)} de-para(s) de ${aba.rotulo.toLowerCase()}`}
          acoes={
            <div className={estilos.buscaLinha}>
              <input
                className={estilos.busca}
                placeholder="Buscar pelo texto de origem…"
                value={busca}
                onChange={(evento) => {
                  setBusca(evento.target.value)
                  setPagina(1)
                }}
              />
              {paginas > 1 && (
                <div className={estilos.paginacao}>
                  <Botao
                    tamanho="pequeno"
                    disabled={pagina <= 1}
                    onClick={() => setPagina((p) => p - 1)}
                  >
                    Anterior
                  </Botao>
                  <span className={estilos.paginaContador}>
                    {pagina} de {paginas}
                  </span>
                  <Botao
                    tamanho="pequeno"
                    disabled={pagina >= paginas}
                    onClick={() => setPagina((p) => p + 1)}
                  >
                    Próxima
                  </Botao>
                </div>
              )}
            </div>
          }
        >
          {consulta.isPending && !consulta.data && <Carregando />}
          {consulta.isError && (
            <Erro erro={consulta.error} aoTentarDeNovo={() => consulta.refetch()} />
          )}
          {consulta.data && (
            <Tabela
              colunas={colunas}
              linhas={consulta.data.results}
              chaveDe={(linha) => linha.id}
              rotulo={`De-paras de ${aba.rotulo}`}
              vazio={busca ? 'Nenhum de-para com esse texto.' : 'Nenhum de-para cadastrado.'}
            />
          )}
        </Cartao>
      </Pilha>
    </Pagina>
  )
}

function Formulario({
  tipo,
  campoAlvo,
  rotuloAlvo,
  alvos,
  registro,
  aoFechar,
}: {
  tipo: TipoDePara
  campoAlvo: string
  rotuloAlvo: string
  alvos: { valor: string; rotulo: string; nota?: string }[]
  registro: Registro | null
  aoFechar: () => void
}) {
  const [origem, setOrigem] = useState(registro?.origem_texto ?? '')
  const [alvo, setAlvo] = useState(registro ? String(registro[campoAlvo] ?? '') : '')
  const [observacao, setObservacao] = useState(registro?.observacao ?? '')

  const salvar = useSalvarDePara(tipo)

  function submeter() {
    salvar.mutate(
      {
        id: registro?.id,
        dados: { origem_texto: origem, [campoAlvo]: Number(alvo), observacao },
      },
      {
        onSuccess: () => {
          if (registro) aoFechar()
          else {
            setOrigem('')
            setObservacao('')
          }
        },
      },
    )
  }

  const pronto = origem.trim() !== '' && alvo !== ''

  return (
    <Cartao
      titulo={registro ? `Editar "${registro.origem_texto}"` : 'Novo de-para'}
      subtitulo="O texto de origem é normalizado ao salvar — espaços das pontas somem e a caixa vira alta. Digite como aparece na planilha."
      acoes={
        registro ? (
          <Botao variante="sutil" onClick={aoFechar}>
            Cancelar edição
          </Botao>
        ) : undefined
      }
    >
      <div className={estilos.formulario}>
        <Entrada
          rotulo="Texto de origem"
          value={origem}
          onChange={(evento) => setOrigem(evento.target.value)}
          placeholder="DIESEL S10"
          dica={
            origem !== origem.trim().toUpperCase() && origem.trim() !== ''
              ? `Será salvo como "${origem.trim().toUpperCase().replace(/\s+/g, ' ')}"`
              : undefined
          }
        />
        <Selecao
          rotulo={rotuloAlvo}
          valor={alvo}
          aoMudar={setAlvo}
          opcoes={alvos}
          vazioRotulo="Escolha…"
        />
        <Entrada
          rotulo="Observação"
          value={observacao}
          onChange={(evento) => setObservacao(evento.target.value)}
          placeholder="Opcional — por que este mapeamento existe"
        />
        <div className={estilos.formularioAcao}>
          <Botao
            variante="primario"
            onClick={submeter}
            disabled={!pronto}
            carregando={salvar.isPending}
          >
            {registro ? 'Salvar' : 'Cadastrar'}
          </Botao>
        </div>
      </div>

      {salvar.isError && <Erro erro={salvar.error} />}
      {salvar.isSuccess && !registro && (
        <Aviso tom="bom">
          Cadastrado. Reprocesse o upload afetado para que as linhas pendentes voltem ao fluxo — o
          staging guardou o conteúdo cru, então não é preciso reenviar o arquivo.
        </Aviso>
      )}
    </Cartao>
  )
}
